import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Capacitor } from '@capacitor/core';

import ElementCount from './ElementCount';
import PlanStatusTitle from '../../../../../user/account/accountModal/plan/PlanStatusTitle';
import PopupPanelAsideSection from '../../../../../components/popupPanel/types/asideSection/PopupPanelAsideSection';

import ElectronLink from '../../../../../components/electron/ElectronLink';
import ApplicationTypeSwitch from '../../../../../platform/components/ApplicationTypeSwitch';

import {
    contentElementCountSelector,
    contentTotalCountSelector,
    contentTotalLimitSelector,
} from '../../../../../user/elementCount/elementCountSelector';
import { getDefaultContentLimit } from '../../../../../../common/users/utils/userPropertyUtils';
import { isUnlimited } from '../../../../../../common/users/contentLimit';

import { InAppPurchases } from '../../../../../../capacitor_plugins/inAppPurchases';
import useAttachInAppPurchases from '../../../../../user/account/checkout/appleInAppPurchases/useAttachInAppPurchases';
import { createAppleCheckoutSession } from '../../../../../user/account/checkout/appleInAppPurchases/appleInAppPurchasesService';

// Constants
import { CHECKOUT_PRODUCTS_ROUTE } from '../../../../../user/account/checkout/checkoutConstants';
import { ELEMENT_COUNT_WARNING_START } from '../../../../../user/elementCount/elementCountConstants';
import { CAPACITOR_IN_APP_PURCHASES_PLUGIN_NAME } from '../../../../../../capacitor_plugins/pluginConstants';

// Types
import { ApplicationType } from '../../../../../../common/platform/platformTypes';
import { ImMNCurrentUser } from '../../../../../user/currentUserSelector';

const IOSUpgradeButton = ({ text }: { text: string }) => {
    useAttachInAppPurchases();

    const onTap = () => {
        (async () => {
            if (!Capacitor.isPluginAvailable(CAPACITOR_IN_APP_PURCHASES_PLUGIN_NAME)) return;

            const checkoutSessionId = await createAppleCheckoutSession();
            InAppPurchases.presentAppleInAppPurchasesStore({ checkoutSessionId });
        })();
    };

    return (
        <a className="StyledButton primary" onClick={onTap}>
            {text}
        </a>
    );
};

const DesktopUpgradeButton = ({ text, onClick }: { text: string; onClick?: () => void }) => (
    <ElectronLink to={CHECKOUT_PRODUCTS_ROUTE} className="StyledButton primary" onClick={onClick}>
        {text}
    </ElectronLink>
);

const mapStateToProps = createStructuredSelector({
    elementCount: contentElementCountSelector,
    contentTotalCount: contentTotalCountSelector,
    contentTotalLimit: contentTotalLimitSelector,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

type BillingTierFreeProps = {
    currentUser: ImMNCurrentUser;
    onUpgradeClick?: () => void;
    shouldFetchCounts?: boolean;
} & ReduxProps;

const BillingTierFree = (props: BillingTierFreeProps) => {
    const {
        currentUser,
        contentTotalCount,
        contentTotalLimit,

        onUpgradeClick,
    } = props;

    const defaultContentLimit = getDefaultContentLimit(currentUser);
    const userHasUnlimitedStorage = isUnlimited(defaultContentLimit);
    const userHasHitCloseToContentLimit = contentTotalCount >= contentTotalLimit - ELEMENT_COUNT_WARNING_START;
    const earnedContent = contentTotalLimit - defaultContentLimit;

    const buttonText = userHasUnlimitedStorage ? 'Upgrade account' : 'Upgrade for unlimited space';

    const CtaButtonIOSCapacitor = () => {
        if (!userHasHitCloseToContentLimit) return null;
        return <IOSUpgradeButton text={buttonText} />;
    };
    const CtaButtonUnavailable = () => null;
    const CtaButtonDesktop = () => <DesktopUpgradeButton text={buttonText} onClick={onUpgradeClick} />;

    return (
        <PopupPanelAsideSection className="billing-status">
            <h3>
                <PlanStatusTitle
                    currentUser={currentUser}
                    contentTotalCount={contentTotalCount}
                    contentTotalLimit={contentTotalLimit}
                />
            </h3>

            <ElementCount {...props} contentTotalLimit={contentTotalLimit} showCountDetail />

            {earnedContent > 0 && (
                <div className="free-content-earned">You&apos;ve earned {earnedContent} extra cards</div>
            )}

            <ApplicationTypeSwitch
                components={{
                    [ApplicationType.iphoneCapacitor]: CtaButtonIOSCapacitor,
                    [ApplicationType.ipadCapacitor]: CtaButtonIOSCapacitor,
                    [ApplicationType.androidCapacitor]: CtaButtonUnavailable,
                    [ApplicationType.androidTabletCapacitor]: CtaButtonUnavailable,
                    [ApplicationType.androidPhonePwa]: CtaButtonUnavailable,
                    [ApplicationType.androidTabletPwa]: CtaButtonUnavailable,
                    [ApplicationType.iframe]: CtaButtonUnavailable,
                    default: CtaButtonDesktop,
                }}
            />
        </PopupPanelAsideSection>
    );
};

export default connector(BillingTierFree);
